/* ==========================================================================
   POLITA AUTOGRU — animations.js
   Motion language: "Sollevare con controllo".
   I contenuti SALGONO entrando, viaggiano lisci, si posano senza rimbalzo.

   Architettura:
   - Guardia prefers-reduced-motion in cima: nessun movimento, contenuti visibili,
     numeri al valore finale.
   - Modern-first, fallback robusto: IntersectionObserver condiviso per i reveal
     (un solo observer, unobserve dopo il trigger). Counters/parallax su rAF.
   - will-change applicato solo durante l'animazione, rimosso a fine.
   - No CLS: si animano solo transform / opacity / clip-path.
   - API ad attributi: data-reveal, data-reveal-group, data-wipe, data-count,
     data-parallax, data-tilt, data-sticky-sequence. Niente animazioni per id.
   ========================================================================== */
(function () {
  'use strict';

  /* Segnala che il motore è partito: disinnesca il failsafe in <head>. */
  window.__politaMotion = true;

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE_POINTER = window.matchMedia('(pointer: fine)').matches;
  var HAS_IO = 'IntersectionObserver' in window;
  var STAGGER = 70; /* ms — coerente con --stagger */

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* rAF helper per coalescere il lavoro nel thread di scroll/resize. */
  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; fn(); });
    };
  }

  /* =======================================================================
     1. REVEAL — i contenuti salgono entrando (rise + fade, wipe immagini)
     ======================================================================= */
  function reveal(el) {
    var isWipe = el.hasAttribute('data-wipe');
    el.style.willChange = isWipe ? 'clip-path, transform' : 'transform, opacity';
    el.classList.add('is-visible');
    function cleanup(e) {
      if (e && e.target !== el) return; /* ignora le transition dei figli (es. img del wipe) */
      el.style.willChange = '';
      el.removeEventListener('transitionend', cleanup);
    }
    el.addEventListener('transitionend', cleanup);
    /* failsafe: se la transitionend non scatta, libera comunque will-change */
    setTimeout(function () { cleanup(); }, 1400);
  }

  function revealGroup(group) {
    var kids = qsa(':scope > .reveal, :scope > [data-reveal]', group);
    if (!kids.length) kids = qsa('.reveal, [data-reveal]', group);
    kids.forEach(function (kid, i) {
      kid.style.transitionDelay = (Math.min(i, 5) * STAGGER) + 'ms';
      reveal(kid);
    });
  }

  function setupReveals() {
    var groups = qsa('[data-reveal-group]');
    var singles = qsa('.reveal, [data-reveal], [data-wipe]').filter(function (el) {
      return !el.closest('[data-reveal-group]');
    });

    if (REDUCE || !HAS_IO) {
      groups.forEach(function (g) { revealGroupNow(g); });
      singles.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var t = en.target;
        if (t.hasAttribute('data-reveal-group')) revealGroup(t);
        else reveal(t);
        io.unobserve(t);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    groups.forEach(function (g) { io.observe(g); });
    singles.forEach(function (el) { io.observe(el); });
  }

  function revealGroupNow(group) {
    var kids = qsa(':scope > .reveal, :scope > [data-reveal]', group);
    if (!kids.length) kids = qsa('.reveal, [data-reveal]', group);
    kids.forEach(function (kid) { kid.classList.add('is-visible'); });
  }

  /* =======================================================================
     2. STAT COUNTER "con peso" — decelera e l'unità si posa dopo
     ======================================================================= */
  function setupCounter(el) {
    var valueEl = el.querySelector('.stat__value') || el;
    var unitEl = el.querySelector('.stat__unit');
    var raw = el.getAttribute('data-count') || el.getAttribute('data-counter') || '';
    var target = parseFloat(String(raw).replace(',', '.'));
    var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
    var useGrouping = el.getAttribute('data-group') !== 'false';
    var prefix = el.getAttribute('data-prefix') || '';
    /* Legacy: suffisso inline (markup senza .stat__unit, es. "120+"). */
    var inlineSuffix = unitEl ? '' : (el.getAttribute('data-suffix') || '');

    function fmt(v) {
      return prefix + v.toLocaleString('it-IT', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: useGrouping
      }) + inlineSuffix;
    }

    if (REDUCE || isNaN(target)) {
      valueEl.textContent = fmt(isNaN(target) ? 0 : target);
      if (unitEl) unitEl.classList.add('is-set');
      return;
    }

    /* La massa detta il tempo: più grande il numero, più lento si posa. */
    var mag = Math.abs(target);
    var duration = Math.min(1500, 1000 + Math.log10(mag + 1) * 150);
    var start = null;
    function easeOut(t) { return 1 - Math.pow(1 - t, 3); } /* decelera verso il target */

    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      valueEl.textContent = fmt(target * easeOut(p));
      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        valueEl.textContent = fmt(target);
        if (unitEl) setTimeout(function () { unitEl.classList.add('is-set'); }, 120);
      }
    }
    requestAnimationFrame(step);
  }

  function setupCounters() {
    var counters = qsa('[data-count], [data-counter]');
    if (!counters.length) return;
    if (REDUCE || !HAS_IO) { counters.forEach(setupCounter); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        setupCounter(en.target);
        io.unobserve(en.target);
      });
    }, { threshold: 0.6 });
    counters.forEach(function (el) { io.observe(el); });
  }

  /* =======================================================================
     3. HERO — il titolo che si issa da una maschera
     ======================================================================= */
  function buildHeroLines(title) {
    var text = title.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return false;

    /* 1) spezza in parole misurabili */
    var words = text.split(' ');
    title.textContent = '';
    var i;
    for (i = 0; i < words.length; i++) {
      var w = document.createElement('span');
      w.className = 'hero__word';
      w.style.display = 'inline-block';
      w.textContent = words[i];
      title.appendChild(w);
      if (i < words.length - 1) title.appendChild(document.createTextNode(' '));
    }

    /* 2) raggruppa per riga visiva (offsetTop) */
    var spans = qsa('.hero__word', title);
    var lines = [], cur = null, top = null;
    spans.forEach(function (sp) {
      var t = sp.offsetTop;
      if (top === null || Math.abs(t - top) > 2) { cur = []; lines.push(cur); top = t; }
      cur.push(sp.textContent);
    });

    /* 3) ricostruisci con maschera per riga */
    title.textContent = '';
    lines.forEach(function (group) {
      var line = document.createElement('span');
      line.className = 'line';
      var inner = document.createElement('span');
      inner.className = 'line__inner';
      inner.textContent = group.join(' ');
      line.appendChild(inner);
      title.appendChild(line);
    });
    return true;
  }

  function setupHero() {
    var heroes = qsa('.hero');
    heroes.forEach(function (hero) {
      if (!REDUCE) {
        var title = hero.querySelector('[data-hero-title]');
        if (title) buildHeroLines(title);
        /* doppio rAF: registra lo stato iniziale prima di sganciare la salita */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { hero.classList.add('is-ready'); });
        });
      } else {
        hero.classList.add('is-ready');
      }
    });
  }

  /* =======================================================================
     4. CARD FLOTTA — micro-tilt che segue il cursore (max 4°)
     ======================================================================= */
  function setupTilt() {
    if (REDUCE || !FINE_POINTER) return;
    var MAX = 4;
    qsa('[data-tilt]').forEach(function (card) {
      var frame = null, nx = 0, ny = 0;
      function apply() {
        frame = null;
        card.style.setProperty('--tilt-y', (nx * MAX).toFixed(2) + 'deg');
        card.style.setProperty('--tilt-x', (-ny * MAX).toFixed(2) + 'deg');
      }
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        nx = (e.clientX - r.left) / r.width - 0.5;
        ny = (e.clientY - r.top) / r.height - 0.5;
        if (!frame) frame = requestAnimationFrame(apply);
      });
      card.addEventListener('mouseleave', function () {
        if (frame) { cancelAnimationFrame(frame); frame = null; }
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  /* =======================================================================
     5. STICKY SEQUENCE — step in crossfade + progress che sale dal basso
     ======================================================================= */
  function setupSticky() {
    qsa('[data-sticky-sequence], [data-sticky-steps]').forEach(function (root) {
      var steps = qsa('[data-step]', root);
      var visuals = qsa('[data-step-visual]', root);
      var fill = root.querySelector('[data-progress-fill]');
      var list = root.querySelector('.process__steps') || (steps[0] && steps[0].parentNode);
      if (!steps.length) return;

      if (REDUCE || !HAS_IO) {
        steps[0].classList.add('is-active');
        if (visuals[0]) visuals[0].classList.add('is-active');
        if (fill) fill.style.transform = 'scaleY(1)';
        return;
      }

      /* attivazione step (crossfade + rise via CSS) */
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var idx = en.target.getAttribute('data-step');
          steps.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-step') === idx); });
          visuals.forEach(function (v) { v.classList.toggle('is-active', v.getAttribute('data-step-visual') === idx); });
        });
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      steps.forEach(function (s) { obs.observe(s); });

      /* indicatore di progresso: il carico che sale dal basso verso l'alto */
      if (fill && list) {
        var update = rafThrottle(function () {
          var r = list.getBoundingClientRect();
          var vh = window.innerHeight;
          var anchor = vh * 0.5;
          var p = (anchor - r.top) / Math.max(r.height, 1);
          p = Math.max(0, Math.min(1, p));
          fill.style.transform = 'scaleY(' + p.toFixed(3) + ')';
        });
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
      }
    });
  }

  /* =======================================================================
     6. PARALLAX — profondità sottilissima, solo desktop/pointer-fine
     ======================================================================= */
  function setupParallax() {
    var els = qsa('[data-parallax]');
    if (!els.length) return;

    var enabled = !REDUCE && FINE_POINTER && window.innerWidth > 900;
    if (!enabled) {
      els.forEach(function (el) { el.style.transform = ''; });
      return;
    }

    var update = rafThrottle(function () {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
        var center = rect.top + rect.height / 2 - vh / 2;
        var shift = -center * speed;
        var max = rect.height * 0.12; /* travel <= --parallax-max */
        if (shift > max) shift = max; else if (shift < -max) shift = -max;
        el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      });
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* =======================================================================
     BOOT — se qualcosa fallisce, rivela tutto (sito sempre fruibile)
     ======================================================================= */
  function init() {
    try {
      setupReveals();
      setupCounters();
      setupHero();
      setupTilt();
      setupSticky();
      setupParallax();
    } catch (err) {
      document.documentElement.classList.remove('js-motion');
      qsa('.reveal, [data-reveal], [data-wipe]').forEach(function (el) { el.classList.add('is-visible'); });
      qsa('.hero').forEach(function (h) { h.classList.add('is-ready'); });
      if (window.console && console.warn) console.warn('animations.js: fallback attivato', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
